import { useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, useSpring, useReducedMotion } from 'framer-motion';
import {
  Users, ShieldCheck, GraduationCap, Wrench, Truck, BarChart3, Lock, ChevronRight
} from 'lucide-react';

const ICONS = {
  'users':          Users,
  'shield-check':   ShieldCheck,
  'graduation-cap': GraduationCap,
  'wrench':         Wrench,
  'truck':          Truck,
  'bar-chart-3':    BarChart3,
};

const cardVariants = {
  hidden:  { opacity: 0, y: 40, scale: 0.95 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.08 + 0.3, duration: 0.6, ease: [0.23, 1, 0.32, 1] },
  }),
};

export function PlatformCard({ platform, index, accessible = true, compact = false, onHoverChange, onOpen }) {
  const cardRef    = useRef(null);
  const reducedMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);

  // ─── 3D Tilt via mouse position ──────────────────────────────────────────
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const scale   = useMotionValue(1);

  const springRotX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const springRotY = useSpring(rotateY, { stiffness: 200, damping: 20 });
  const springScale = useSpring(scale,  { stiffness: 300, damping: 25 });

  // Glow follow
  const glowX = useMotionValue(50);
  const glowY = useMotionValue(50);

  const handleMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    rotateY.set((x - 0.5) * 22);
    rotateX.set((y - 0.5) * -16);
    glowX.set(x * 100);
    glowY.set(y * 100);
  };

  const handleMouseEnter = () => {
    setHovered(true); scale.set(1.03);
    onHoverChange?.(platform.id);
  };
  const handleMouseLeave = () => {
    setHovered(false);
    rotateX.set(0); rotateY.set(0); scale.set(1);
    glowX.set(50);  glowY.set(50);
    onHoverChange?.(null);
  };

  const Icon     = ICONS[platform.icon] || BarChart3;
  const isLocked = !accessible;
  // Optional brand gradient (color → colorEnd); falls back to solid accents
  const accentEnd = platform.colorEnd || platform.color;

  const glowBg = useTransform(
    [glowX, glowY],
    ([gx, gy]) =>
      `radial-gradient(circle at ${gx}% ${gy}%, ${platform.color}18 0%, transparent 70%)`
  );

  return (
    <motion.div
      ref={cardRef}
      data-testid="platform-card"
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      layoutId={reducedMotion ? undefined : `platform-${platform.id}`}
      onClick={() => {
        // Cards no longer navigate directly — they open the section panel
        // (dashboard area + platform), which handles navigation/SSO handoff.
        if (isLocked) return;
        onOpen?.(platform.id);
      }}
      className={`
        relative rounded-2xl overflow-hidden cursor-pointer
        border transition-colors duration-300
        ${isLocked
          ? 'border-white/5 opacity-50 cursor-not-allowed'
          : 'border-white/8 hover:border-[var(--color)]/30'
        }
      `}
      style={{
        rotateX:    springRotX,
        rotateY:    springRotY,
        scale:      springScale,
        perspective: 1000,
        transformStyle: 'preserve-3d',
        '--color': platform.color,
        background: 'rgba(12, 20, 40, 0.58)',
        backdropFilter: 'blur(28px) saturate(180%)',
        boxShadow: hovered && !isLocked
          ? `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px ${platform.color}30, 0 0 40px ${platform.color}15`
          : '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      {/* Glow follow */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-0"
        style={{ background: glowBg }}
      />

      {/* Top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${platform.color}, ${accentEnd}, transparent)` }}
      />

      {/* Card Content */}
      <div className={`relative z-10 flex flex-col h-full ${compact ? 'p-4 gap-3' : 'p-6 gap-4'}`}>
        {/* Header row */}
        <div className="flex items-start justify-between">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{
              background: `${platform.color}15`,
              border:     `1px solid ${platform.color}30`,
              boxShadow:  hovered ? `0 0 20px ${platform.color}30` : 'none',
              transition: 'box-shadow 0.3s',
            }}
          >
            <Icon size={22} style={{ color: platform.color }} strokeWidth={1.5} />
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* Status dot */}
            <div className="flex items-center gap-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: isLocked ? '#8199B4' : '#22C55E' }}
              />
              <span className="text-2xs font-mono text-gl-muted uppercase tracking-widest">
                {isLocked ? 'RESTRICTED' : 'ONLINE'}
              </span>
            </div>
            {/* Badge */}
            <div
              className="px-2 py-0.5 rounded text-2xs font-mono font-bold tracking-widest"
              style={{
                background: platform.colorEnd
                  ? `linear-gradient(90deg, ${platform.color}18, ${platform.colorEnd}18)`
                  : `${platform.color}18`,
                color: platform.color,
              }}
            >
              {platform.badge}
            </div>
          </div>
        </div>

        {/* Title */}
        <div>
          <h3 className="text-lg font-display font-semibold text-gl-ghost leading-tight">
            {platform.title}
          </h3>
          <p className="text-xs text-gl-muted font-mono mt-0.5 tracking-wide uppercase">
            {platform.subtitle}
          </p>
        </div>

        {/* Description (hidden in the compact landing grid) */}
        {!compact && (
          <p className="text-sm text-gl-muted leading-relaxed line-clamp-2 flex-1">
            {platform.description}
          </p>
        )}

        {/* Footer row */}
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            {isLocked ? <Lock size={12} className="text-gl-muted" /> : null}
            <span className="text-xs text-gl-muted">{platform.accessLabel}</span>
          </div>
          <motion.div
            animate={{ x: hovered && !isLocked ? 4 : 0 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <ChevronRight size={14} style={{ color: isLocked ? '#8199B4' : platform.color }} />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
